import { Injectable } from "@angular/core";

@Injectable({
  providedIn: "root",
})
export class CacheService {
  private defaultExpirationTime = 20 * 60 * 1000; // 20 minutes default

  /**
   * Set data in cache with expiration
   */
  public setCacheData<T>(key: string, data: T, expirationTime?: number): void {
    const cacheItem = {
      data: data,
      timestamp: new Date().getTime(),
    };
    try {
      localStorage.setItem(key, JSON.stringify(cacheItem));
    } catch (error) {
      console.warn("Failed to store cache data:", error);
    }
  }

  /**
   * Get data from cache if it exists and is not expired
   */
  public getCacheData<T>(key: string, expirationTime?: number): T | null {
    const expiration = expirationTime ?? this.defaultExpirationTime;
    try {
      const cacheItemJson = localStorage.getItem(key);
      if (!cacheItemJson) return null;

      const cacheItem = JSON.parse(cacheItemJson);
      const currentTime = new Date().getTime();

      if (currentTime - cacheItem.timestamp > expiration) {
        // Cache expired, remove it
        localStorage.removeItem(key);
        return null;
      }

      return cacheItem.data as T;
    } catch (error) {
      console.warn("Failed to retrieve cache data:", error);
      return null;
    }
  }

  /**
   * Generate a cache key with prefix and optional additional keys
   */
  public generateCacheKey(
    prefix: string,
    mainKey: string | Number,
    additionalKey?: string | Number
  ): string {
    return `${prefix}${mainKey}${additionalKey ? "_" + additionalKey : ""}`;
  }

  /**
   * Clear cache items with a specific prefix
   */
  public clearCacheWithPrefix(prefix: string): void {
    try {
      // Loop through all localStorage items and remove those with our prefix
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefix)) {
          localStorage.removeItem(key);
        }
      }
    } catch (error) {
      console.warn("Failed to clear cache:", error);
    }
  }

  /**
   * Clear cache items with prefix and specific key
   */
  public clearCacheWithPrefixAndKey(
    prefix: string,
    keyPart: string | number
  ): void {
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefix) && key.includes(`_${keyPart}`)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => localStorage.removeItem(key));
    } catch (error) {
      console.warn("Failed to clear cache for specific key:", error);
    }
  }

  /**
   * Remove a specific cache item
   */
  public removeCacheItem(key: string): void {
    localStorage.removeItem(key);
  }
}
